import React, { useEffect, useState, useRef } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';

// Importamos la lógica de negocio (Ajusta las rutas según tu proyecto)
import { 
    GetScoreState, UpdateScore, UpdateScoreStyle, 
    SendGoalEvent, SendCardEvent, SendSubstitutionEvent, TriggerScoreNotification
} from '../../../utilities/BL/Score';
import { GetTeams, GetPlayersByTeam } from '../../../utilities/BL/SportPlayer';
import { Events } from './Events';


const Score = ({ getOutOfTheDynamic, isEmbedded }) => {
    const { showMessage } = useFloatMessage();
    
    // --- ESTADO LÓGICO DEL MARCADOR ---
    const [scoreState, setScoreState] = useState({
        homeScore: 0, awayScore: 0,
        timerSeconds: 0, isTimerRunning: false,
        addedTime: 0, periodName: '1er Tiempo',
        homeTeamName: 'Local', awayTeamName: 'Visita',
        scale: 1.0, backgroundColor: 'rgba(15, 23, 42, 0.95)', showSponsor: true,
        textColor: '#ffffff', numberColor: '#00ddff', timerBgColor: 'rgba(0, 0, 0, 0.4)'
    });

    // --- ESTADOS DE CATÁLOGOS (BD) ---
    const [teams, setTeams] = useState([]);
    const [homePlayers, setHomePlayers] = useState([]);
    const [awayPlayers, setAwayPlayers] = useState([]);
    const [selectedHomeTeam, setSelectedHomeTeam] = useState('');
    const [selectedAwayTeam, setSelectedAwayTeam] = useState('');

    // --- ESTADOS DE CONTROL DE INTERFAZ ---
    const [localTimer, setLocalTimer] = useState(0);
    const [addedTimeInput, setAddedTimeInput] = useState('');
    const timerIntervalRef = useRef(null);

    // --- ESTADOS PARA EVENTOS / NOTIFICACIONES ---
    const [animEnabled, setAnimEnabled] = useState(true);

    // -------------------------------------------------------------------------
    // 1. CARGA INICIAL DE DATOS
    // -------------------------------------------------------------------------
    useEffect(() => {
        const loadInitialData = async () => {
            // 1. Cargar estado actual del marcador
            const resScore = await GetScoreState();
            if (resScore.success) {
                setScoreState(resScore.data);
                setLocalTimer(resScore.data.timerSeconds);
                setAddedTimeInput(resScore.data.addedTime);
            } else {
                showMessage(resScore.message, MESSAGE_TYPE.BAD);
            }

            // 2. Cargar catálogo de equipos
            const resTeams = await GetTeams();
            if (resTeams.success) {
                setTeams(resTeams.data);
            }
        };
        loadInitialData();
        return () => clearInterval(timerIntervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -------------------------------------------------------------------------
    // 2. SINCRONIZACIÓN DEL CRONÓMETRO LOCAL DEL ADMIN
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (scoreState.isTimerRunning) {
            timerIntervalRef.current = setInterval(() => {
                setLocalTimer(prev => prev + 1);
            }, 1000);
        } else {
            clearInterval(timerIntervalRef.current);
        }
        return () => clearInterval(timerIntervalRef.current);
    }, [scoreState.isTimerRunning]);

    const formatTime = (totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const secs = (totalSeconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    // -------------------------------------------------------------------------
    // 3. MÉTODOS DE ACTUALIZACIÓN (Marcador y Tiempo)
    // -------------------------------------------------------------------------
    const handleUpdateScore = async (payload) => {
        const res = await UpdateScore(payload);
        if (res.success) {
            setScoreState(res.data);
            if (payload.timerSeconds !== undefined) setLocalTimer(payload.timerSeconds);
        } else {
            showMessage(res.message, MESSAGE_TYPE.BAD);
        }
    };

    const handleUpdateStyle = async (payload) => {
        const res = await UpdateScoreStyle(payload);
        if (res.success) setScoreState(res.data);
        else showMessage(res.message, MESSAGE_TYPE.BAD);
    };

    const changeScore = async (team, amount) => {
        let newScore = team === 'home' ? scoreState.homeScore + amount : scoreState.awayScore + amount;
        if (newScore < 0) newScore = 0;

        const payload = team === 'home' ? { homeScore: newScore } : { awayScore: newScore };
        await handleUpdateScore(payload);

        // Si sube el marcador y la animación está activa, disparamos la celebración genérica
        if (amount > 0 && animEnabled) {
            await TriggerScoreNotification({ type: 'goal', htmlContent: '', durationSeconds: 9 });
        }
    };

    //const toggleTimer = () => handleUpdateScore({ isTimerRunning: !scoreState.isTimerRunning });
    const toggleTimer = () => {
        const nextRunningState = !scoreState.isTimerRunning;
        
        // Sincronizamos enviando el estado de ejecución junto con el tiempo exacto acumulado
        handleUpdateScore({ 
            isTimerRunning: nextRunningState,
            timerSeconds: localTimer 
        });
    };
    const resetTimer = () => handleUpdateScore({ timerSeconds: 0, isTimerRunning: false, addedTime: 0 });
    const applyAddedTime = () => handleUpdateScore({ addedTime: parseInt(addedTimeInput) || 0 });
    const changePeriod = (name) => handleUpdateScore({ periodName: name });

    // -------------------------------------------------------------------------
    // 4. MÉTODOS DE EQUIPOS Y JUGADORES
    // -------------------------------------------------------------------------
    const handleTeamSelection = async (teamType, teamId) => {
        const teamName = teams.find(t => t.id === parseInt(teamId))?.name || (teamType === 'home' ? 'Local' : 'Visita');
        
        if (teamType === 'home') {
            setSelectedHomeTeam(teamId);
            handleUpdateScore({ homeTeamName: teamName });
            if (teamId) {
                const res = await GetPlayersByTeam(teamId);
                if (res.success) setHomePlayers(res.data);
            } else { setHomePlayers([]); }
        } else {
            setSelectedAwayTeam(teamId);
            handleUpdateScore({ awayTeamName: teamName });
            if (teamId) {
                const res = await GetPlayersByTeam(teamId);
                if (res.success) setAwayPlayers(res.data);
            } else { setAwayPlayers([]); }
        }
    };



    // -------------------------------------------------------------------------
    // RENDERIZADO DEL PANEL
    // -------------------------------------------------------------------------
    const content = (
        <div className="container-fluid pb-5">
                <div className="row g-4">
                    {/* --- COLUMNA 1: CRONÓMETRO --- */}
                    <div className="col-md-6">
                        <div className="card shadow-sm border-0 rounded-4 h-100 bg-light">
                            <div className="card-body">
                                <h6 className="fw-bold text-primary mb-3 text-uppercase" style={{fontSize: '0.8rem'}}>Cronómetro</h6>
                                
                                <div className="text-center bg-dark text-white rounded-3 py-2 mb-3 fs-2 fw-bold font-monospace">
                                    {formatTime(localTimer)}
                                </div>

                                <div className="d-flex gap-2 mb-4">
                                    <button className={`btn w-50 fw-bold ${scoreState.isTimerRunning ? 'btn-warning' : 'btn-success'}`} onClick={toggleTimer}>
                                        <i className={`bi ${scoreState.isTimerRunning ? 'bi-pause-fill' : 'bi-play-fill'}`}></i> {scoreState.isTimerRunning ? 'Pausar' : 'Iniciar'}
                                    </button>
                                    <button className="btn btn-danger w-50 fw-bold" onClick={resetTimer}>
                                        <i className="bi bi-arrow-counterclockwise"></i> Reset
                                    </button>
                                </div>

                                <h6 className="fw-bold text-muted mb-2 text-uppercase" style={{fontSize: '0.75rem'}}>Periodo: {scoreState.periodName}</h6>
                                <div className="d-flex gap-1 mb-4 flex-wrap">
                                    <button className="btn btn-outline-secondary btn-sm flex-fill" onClick={() => changePeriod('1er Tiempo')}>1º</button>
                                    <button className="btn btn-outline-secondary btn-sm flex-fill" onClick={() => changePeriod('2do Tiempo')}>2º</button>
                                    <button className="btn btn-outline-secondary btn-sm flex-fill" onClick={() => changePeriod('T. Extra')}>TE</button>
                                    <button className="btn btn-outline-secondary btn-sm flex-fill" onClick={() => changePeriod('Penales')}>Pen</button>
                                </div>

                                <h6 className="fw-bold text-success mb-2 text-uppercase" style={{fontSize: '0.75rem'}}>Adición (Minutos)</h6>
                                <div className="input-group">
                                    <input type="number" className="form-control text-center fw-bold" placeholder="0" value={addedTimeInput} onChange={(e) => setAddedTimeInput(e.target.value)} />
                                    <button className="btn btn-success fw-bold" onClick={applyAddedTime}>Aplicar</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- COLUMNA 2: MARCADOR --- */}
                    <div className="col-md-6">
                        <div className="card shadow-sm border-0 rounded-4 h-100 bg-light">
                            <div className="card-body">
                                <h6 className="fw-bold text-warning mb-3 text-uppercase" style={{fontSize: '0.8rem'}}>Marcador</h6>
                                
                                <div className="form-check form-switch mb-3">
                                    <input className="form-check-input" type="checkbox" role="switch" checked={animEnabled} onChange={(e) => setAnimEnabled(e.target.checked)} />
                                    <label className="form-check-label small fw-bold text-muted">Animación automática al sumar gol</label>
                                </div>

                                {/* LOCAL */}
                                <div className="d-flex align-items-center bg-white border rounded p-2 mb-3 shadow-sm">
                                    <div className="flex-grow-1 text-center">
                                        <div className="small fw-bold text-muted text-uppercase text-truncate px-2" style={{maxWidth: '120px'}}>{scoreState.homeTeamName}</div>
                                        <div className="fs-3 fw-bold">{scoreState.homeScore}</div>
                                    </div>
                                    <div className="d-flex flex-column gap-1">
                                        <button className="btn btn-primary btn-sm fw-bold" onClick={() => changeScore('home', 1)}><i className="bi bi-plus"></i></button>
                                        <button className="btn btn-danger btn-sm fw-bold" onClick={() => changeScore('home', -1)} disabled={scoreState.homeScore <= 0}><i className="bi bi-dash"></i></button>
                                    </div>
                                </div>

                                {/* VISITA */}
                                <div className="d-flex align-items-center bg-white border rounded p-2 mb-4 shadow-sm">
                                    <div className="flex-grow-1 text-center">
                                        <div className="small fw-bold text-muted text-uppercase text-truncate px-2" style={{maxWidth: '120px'}}>{scoreState.awayTeamName}</div>
                                        <div className="fs-3 fw-bold">{scoreState.awayScore}</div>
                                    </div>
                                    <div className="d-flex flex-column gap-1">
                                        <button className="btn btn-primary btn-sm fw-bold" onClick={() => changeScore('away', 1)}><i className="bi bi-plus"></i></button>
                                        <button className="btn btn-danger btn-sm fw-bold" onClick={() => changeScore('away', -1)} disabled={scoreState.awayScore <= 0}><i className="bi bi-dash"></i></button>
                                    </div>
                                </div>

                                <button className="btn btn-warning w-100 fw-bold shadow-sm" onClick={() => TriggerScoreNotification({ type: 'promo', htmlContent: '<div style="font-size: 2rem; font-weight: 900; color: #facc15; text-transform: uppercase;">★ PATROCINADOR LIVE ★</div>', durationSeconds: 9 })}>
                                    <i className="bi bi-star-fill"></i> Lanzar Promo Patrocinador
                                </button>

                                <div className="mt-3 border-top pt-2">
                                     <label className="form-label small fw-bold text-muted"><i className="bi bi-image"></i> Logo Promo Patrocinador (Máx 800KB)</label>
                                     <input 
                                         type="file" 
                                         accept="image/*" 
                                         className="form-control form-control-sm bg-white border-secondary mb-2"
                                         onChange={(e) => {
                                             const file = e.target.files[0];
                                             if (!file) return;
                                             if (file.size > 800 * 1024) {
                                                 showMessage('La imagen es demasiado grande. Elige una menor a 800KB.', MESSAGE_TYPE.BAD);
                                                 return;
                                             }
                                             const reader = new FileReader();
                                             reader.onload = (event) => {
                                                 handleUpdateStyle({ promoLogo: event.target.result });
                                                 showMessage('Imagen promocional de patrocinador actualizada.', MESSAGE_TYPE.SUCCESS);
                                             };
                                             reader.readAsDataURL(file);
                                         }}
                                     />
                                     {scoreState.promoLogo && (
                                         <div className="d-flex align-items-center gap-2 mt-2 bg-white p-2 border rounded">
                                             <img 
                                                 src={scoreState.promoLogo} 
                                                 alt="Preview Promo" 
                                                 style={{ maxHeight: '35px', maxWidth: '100px', objectFit: 'contain' }} 
                                             />
                                             <button 
                                                 type="button"
                                                 className="btn btn-outline-danger btn-xs py-0 flex-fill"
                                                 onClick={() => {
                                                     handleUpdateStyle({ promoLogo: "" });
                                                     showMessage('Imagen promocional eliminada.', MESSAGE_TYPE.SUCCESS);
                                                 }}
                                             >
                                                 Remover
                                             </button>
                                         </div>
                                     )}
                                 </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- SECCIÓN 1: SELECCIÓN DE EQUIPOS --- */}
                <div className="card shadow-sm border-0 rounded-4 mb-4 mt-4">
                    <div className="card-header bg-dark text-white fw-bold"><i className="bi bi-shield-shaded"></i> Configuración de Partido</div>
                    <div className="card-body row g-3">
                        <div className="col-md-6">
                            <label className="form-label small fw-bold text-primary">Equipo Local</label>
                            <select className="form-select" value={selectedHomeTeam} onChange={(e) => handleTeamSelection('home', e.target.value)}>
                                <option value="">-- Seleccionar Equipo --</option>
                                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small fw-bold text-danger">Equipo Visitante</label>
                            <select className="form-select" value={selectedAwayTeam} onChange={(e) => handleTeamSelection('away', e.target.value)}>
                                <option value="">-- Seleccionar Equipo --</option>
                                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* --- SECCIÓN 3: CONTROL DE EVENTOS EN VIVO --- */}
                <Events 
                    homePlayers={homePlayers} 
                    awayPlayers={awayPlayers} 
                    homeTeamName={scoreState.homeTeamName} 
                    awayTeamName={scoreState.awayTeamName} 
                    currentMinute={formatTime(localTimer)} 
                    onScoreChange={changeScore}
                />

                {/* --- SECCIÓN 4: ESTILOS VISUALES --- */}
                <div className="card shadow-sm border-0 rounded-4 mt-4">
                    <div className="card-body">
                        <h6 className="fw-bold text-muted mb-3"><i className="bi bi-palette-fill"></i> Personalización Visual de la Pantalla</h6>
                        <div className="row g-3 align-items-center">
                            <div className="col-md-4 d-flex align-items-center gap-2">
                                <label className="form-label small fw-bold mb-0">Tamaño (Zoom):</label>
                                <input type="range" className="form-range" min="0.5" max="2.0" step="0.1" value={scoreState.scale} onChange={(e) => handleUpdateStyle({ scale: parseFloat(e.target.value) })} />
                                <span className="badge bg-secondary">{Math.round(scoreState.scale * 100)}%</span>
                            </div>
                            <div className="col-md-4 d-flex align-items-center gap-2">
                                <label className="form-label small fw-bold mb-0">Color de Fondo:</label>
                                <input type="color" className="form-control form-control-color" value={scoreState.backgroundColor.length === 7 ? scoreState.backgroundColor : '#0f172a'} onChange={(e) => handleUpdateStyle({ backgroundColor: e.target.value })} title="Elige un color sólido" />
                            </div>
                            <div className="col-md-4">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" role="switch" checked={scoreState.showSponsor} onChange={(e) => handleUpdateStyle({ showSponsor: e.target.checked })} />
                                    <label className="form-check-label small fw-bold">Mostrar Logo del Patrocinador</label>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3 align-items-center mt-2 border-top pt-2">
                            <div className="col-md-4 d-flex align-items-center gap-2">
                                <label className="form-label small fw-bold mb-0">Color de Texto:</label>
                                <input type="color" className="form-control form-control-color border-secondary" value={scoreState.textColor || '#ffffff'} onChange={(e) => handleUpdateStyle({ textColor: e.target.value })} title="Elige el color de los nombres de los equipos" />
                            </div>
                            <div className="col-md-4 d-flex align-items-center gap-2">
                                <label className="form-label small fw-bold mb-0">Color de Goles:</label>
                                <input type="color" className="form-control form-control-color border-secondary" value={scoreState.numberColor || '#00ddff'} onChange={(e) => handleUpdateStyle({ numberColor: e.target.value })} title="Elige el color de los goles" />
                            </div>
                            <div className="col-md-4 d-flex align-items-center gap-2">
                                <label className="form-label small fw-bold mb-0">Fondo de Tiempo:</label>
                                <input type="color" className="form-control form-control-color border-secondary" value={scoreState.timerBgColor && scoreState.timerBgColor.startsWith('#') ? scoreState.timerBgColor : '#000000'} onChange={(e) => handleUpdateStyle({ timerBgColor: e.target.value })} title="Elige el fondo del cronómetro" />
                            </div>
                        </div>

                        {scoreState.showSponsor && (
                            <div className="mt-3 border-top pt-3">
                                <div className="row g-3 align-items-center">
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted"><i className="bi bi-image"></i> Subir Logo Patrocinador (PNG/JPG)</label>
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            className="form-control form-control-sm bg-dark text-white border-secondary"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                if (file.size > 800 * 1024) {
                                                    showMessage('La imagen es demasiado grande. Elige una menor a 800KB.', MESSAGE_TYPE.BAD);
                                                    return;
                                                }
                                                const reader = new FileReader();
                                                reader.onload = (event) => {
                                                    handleUpdateStyle({ sponsorLogo: event.target.result });
                                                    showMessage('Logotipo del patrocinador actualizado.', MESSAGE_TYPE.SUCCESS);
                                                };
                                                reader.readAsDataURL(file);
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6 d-flex align-items-center gap-3">
                                        {scoreState.sponsorLogo && (
                                            <>
                                                <img 
                                                    src={scoreState.sponsorLogo} 
                                                    alt="Preview Logo" 
                                                    style={{ maxHeight: '40px', maxWidth: '120px', objectFit: 'contain', border: '1px solid rgba(255,255,255,0.1)', padding: '2px', background: '#fff' }} 
                                                />
                                                <button 
                                                    className="btn btn-outline-danger btn-sm fw-bold py-1"
                                                    onClick={() => {
                                                        handleUpdateStyle({ sponsorLogo: "" });
                                                        showMessage('Logotipo removido.', MESSAGE_TYPE.SUCCESS);
                                                    }}
                                                >
                                                    Remover Logo
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
    );

    if (isEmbedded) {
        return content;
    }

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            {content}
        </SecondaryBase>
    );
};

export { Score };




















// import { SecondaryBase } from "../Commons/SecondaryBase";

// const Score = () => {
//     return (
//         <SecondaryBase>
//             <section className='AdmRunToGoal__Settings pb-5'>
//                 <p className='AdmRunToGoal__Title'>Marcador de fútbol</p>

//                 <div className="">
//                     <div className="">
//                         <p>Vista previa</p>

//                         <button type="button" className="Btn__Adsl--RedOutlineTransparent"><i className="bi bi-eye"></i> Mostrar en pantalla</button>
//                         <button type="button" className="Btn__Adsl--RedOutlineTransparent"><i className="bi bi-eye-slash"></i> Ocultar de pantalla</button>
//                     </div>

//                     <p className="text-center mt-4">Acciones:</p>
//                     <div className="Score__Admin__Players">
//                         <div className="Score__Admin__Players__PlayerCards">
//                             <div className="Score__Admin__Players__PlayerCards__Card">
//                                 {/* <img src="" className="Score__Admin__Players__PlayerCards__Card__Image" alt="Jugador" /> */}
//                                 <span className="Score__Admin__Players__PlayerCards__Card__Image"></span>
//                                 <p className="Score__Admin__Players__PlayerCards__Card__Name">Julio Mora</p>
//                                 <p className="Score__Admin__Players__PlayerCards__Card__Number">#15</p>
//                             </div>
//                         </div>
//                     </div>

                    

//                     <p className="text-center mt-4">Cronómetro:</p>
                    
//                     <div className="">
//                         <label>Tiempo inicial:</label>
//                         <div className="Score__Admin__Cronometer">
//                             <input type="text" className="Score__Admin__Time__Controls" placeholder="45" /> : <input type="text" className="Score__Admin__Time__Controls" placeholder="00" /> minutos
//                         </div>
//                         <button type="button" className="Btn__Adsl--Red mt-2">Iniciar / Pausar</button>
//                         <button type="button" className="Btn__Adsl--RedOutlineTransparent mt-2">Reiniciar</button>

//                         <label className="mt-4">Periodo:</label>
//                         <div className="Score__Admin__Periods">
//                             <button type="button" className="Btn__Adsl--Red Score__Admin__Periods__Buttons"><i className="bi bi-check-circle"></i> 1°</button>
//                             <button type="button" className="Btn__Adsl--RedOutlineTransparent Score__Admin__Periods__Buttons"><i className="bi bi-check-circle"></i> 2°</button>
//                             <button type="button" className="Btn__Adsl--RedOutlineTransparent Score__Admin__Periods__Buttons"><i className="bi bi-check-circle"></i> T. Extra</button>
//                             <button type="button" className="Btn__Adsl--RedOutlineTransparent Score__Admin__Periods__Buttons"><i className="bi bi-check-circle"></i> Penales</button>
//                         </div>

//                         <label className="mt-4">Tiempo extra:</label>
//                         <div className="Score__Admin__Time">
//                             <input type="text" className="Score__Admin__Time__Controls" placeholder="45" /> : <input type="text" className="Score__Admin__Time__Controls" placeholder="00" /> minutos
//                             {/* <button type="button" className="Btn__Adsl--RedOutlineTransparent">Iniciar / Pausar</button>
//                             <button type="button" className="Btn__Adsl--RedOutlineTransparent">Reiniciar</button> */}
//                         </div>
//                     </div>
                    

//                 {/* Imágenes */}
//                 {/* 
//                     --- Equipo ---
//                     Logo Equipo #1
//                     Nombre Equipo #1
                    
//                     Logo Equipo #2
//                     Nombre Equipo #2

//                     Jugadores:
//                     - Foto
//                     - Nombre
//                     - Número

//                     Acciones:
//                     Tarjeta amarilla
//                     Tarjeta roja
//                     Gol
//                     Autogol

//                     Cambio:
//                     Entra
//                     Sale
//                     (Cargar como la IA Cam)

//                     --- Cronómetro ---
//                     Tiempo inicial
//                     Iniciar/Pausar
//                     Resetear
//                     Periodo: 1°, 2°, Tiempo extra, Penales
//                     Tiempo extra: X minutos

//                     --- Marcador ---
//                     Local
//                     Animación
//                     Gol local
//                     - (restar gol)

//                     Visita
//                     Animación
//                     Gol visita
//                     - (restar gol)

//                     --- Publicidad ---
//                     Logo en marcador
//                     Logo en banner promocional
//                     - Btn Mostrar banner promocional
//                     - Tiempo (9s default)
//                 */}

//                 </div>
//             </section>
//         </SecondaryBase>
//     )
// }

// export { Score };